/** Manages event handlers for task items, switching between touch-based and mouse-based interactions based on device type. */
// @ts-check
import { useMemo } from 'react';
import useLongPress from '@/features/tasks/hooks/ui/use-interaction-long-press';
import { composePointerHandlers } from '@/features/tasks/hooks/ui/composePointerHandlers';
import { useTaskUI } from '@/features/tasks/context/TaskUIContext';
import { detectTouchDevice } from '@/hooks/ui/detectTouchDevice';

/**
 * Encapsulates all interaction event handlers for a TaskItemBase.
 * Switches between mobile long-press handlers and desktop click handler
 * based on actual touch capability (not layout breakpoints like battlefield).
 *
 * @param {{
 *   task: Task,
 *   attributes: any,
 *   listeners: any
 * }} props
 */
export function useTaskItemEvents({ task, attributes, listeners }) {
  const { onTaskClick, onSelectSingle, onToggleInSelection } = useTaskUI();

  const isTouchDevice = useMemo(() => detectTouchDevice(), []);

  const handleLongPress = () => {
    if (!task) return;
    onToggleInSelection(task.id);
  };

  const handleShortClick = () => {
    if (!task) return;
    onSelectSingle(task.id);
  };

  const mobileLongPressHandlers = useLongPress(
    handleLongPress,
    handleShortClick,
    { ms: 250 },
  );

  const handleDesktopClick = (event) => {
    if (!task) return;
    onTaskClick(task.id, event);
  };

  const eventHandlers = isTouchDevice
    ? {
        ...attributes,
        ...mobileLongPressHandlers,
        ...listeners,
        ...composePointerHandlers(listeners, mobileLongPressHandlers),
      }
    : { ...attributes, ...listeners, onClick: handleDesktopClick };

  return { eventHandlers, isTouchDevice };
}
